import React from 'react';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { RichTextEditor } from 'src/design/molecules/RichTextEditor';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Switch } from 'src/design/atoms/Switch';
import { TextInput } from 'src/design/atoms/TextInput';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import { RecipientPicker } from 'src/app/workflows/NotificationComposer/RecipientPicker';
import { hasExternalRecipient } from 'src/app/workflows/recipients';
import {
  NotificationCompositionRequest,
  NotificationPreviewDto,
  RecipientDto as NotificationRecipientDto,
  UserDto,
} from 'src/api/FiretrolApiClient';
import { showToast } from 'src/utils';

export interface INotificationComposerProps {
  opened: boolean;
  onClose: () => void;
  preview: NotificationPreviewDto | null;
  jobUsers: UserDto[];
  customerNumber: string;
  submitting?: boolean;
  title?: string;
  toPlaceholder?: string;
  attachPdfLabel?: string;
  onSend: (request: NotificationCompositionRequest) => void | Promise<void>;
  onSkip: (request: NotificationCompositionRequest) => void | Promise<void>;
  onReloadPreview?: (
    external: boolean,
  ) => Promise<NotificationPreviewDto | null>;
}

/** @registry Module-agnostic email composer modal for workflow notifications: To/CC recipient pickers, subject, message, attach-PDF switch, and Send/Skip actions prefilled from a notification preview. Regenerates the body when the To list flips between internal and external recipients. */
export const NotificationComposer: React.FC<INotificationComposerProps> = ({
  opened,
  onClose,
  preview,
  jobUsers,
  customerNumber,
  submitting = false,
  title = 'Compose email',
  toPlaceholder = 'Add recipients',
  attachPdfLabel = 'Attach generated PDF',
  onSend,
  onSkip,
  onReloadPreview,
}) => {
  const [to, setTo] = React.useState<NotificationRecipientDto[]>([]);
  const [cc, setCc] = React.useState<NotificationRecipientDto[]>([]);
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [attachPdf, setAttachPdf] = React.useState(false);
  const loadedExternalRef = React.useRef(false);
  const pendingFlipRef = React.useRef<{
    external: boolean;
    bodyWasEdited: boolean;
  } | null>(null);

  React.useEffect(() => {
    if (!opened) return;
    const pending = pendingFlipRef.current;
    if (pending) {
      pendingFlipRef.current = null;
      setSubject(preview?.subject ?? '');
      setMessage(preview?.bodyHtml ?? '');
      setAttachPdf(preview?.attachPdf ?? false);
      loadedExternalRef.current = pending.external;
      if (pending.bodyWasEdited) {
        showToast(
          'The message body was regenerated for the new recipient type.',
          { intent: 'warning' },
        );
      }
      return;
    }
    setTo(preview?.to ?? []);
    setCc(preview?.cc ?? []);
    setSubject(preview?.subject ?? '');
    setMessage(preview?.bodyHtml ?? '');
    setAttachPdf(preview?.attachPdf ?? false);
    loadedExternalRef.current = hasExternalRecipient(preview?.to ?? []);
  }, [opened, preview]);

  const handleToChange = (next: NotificationRecipientDto[]) => {
    setTo(next);
    const nextExternal = hasExternalRecipient(next);
    if (nextExternal === loadedExternalRef.current || !onReloadPreview) return;
    const bodyWasEdited =
      subject !== (preview?.subject ?? '') ||
      message !== (preview?.bodyHtml ?? '');
    pendingFlipRef.current = { external: nextExternal, bodyWasEdited };
    void onReloadPreview(nextExternal);
  };

  const buildRequest = (
    skipEmail: boolean,
  ): NotificationCompositionRequest => ({
    eventType: preview?.eventType ?? '',
    subject,
    bodyHtml: message,
    to,
    cc,
    bcc: [],
    attachPdf,
    skipEmail,
  });

  return (
    <BaseModal opened={opened} onClose={onClose} title={title} size="lg">
      {preview === null ? (
        <Group justify="center" py="xl">
          <Loader aria-label="Loading preview" />
        </Group>
      ) : (
        <Stack gap="sm">
          <RecipientPicker
            label="To"
            placeholder={toPlaceholder}
            value={to}
            onChange={handleToChange}
            jobUsers={jobUsers}
            customerNumber={customerNumber}
          />
          <RecipientPicker
            label="CC"
            placeholder="Add CC recipients"
            value={cc}
            onChange={setCc}
            jobUsers={jobUsers}
            customerNumber={customerNumber}
          />
          <TextInput
            label="Subject"
            value={subject}
            onChange={(event) => setSubject(event.currentTarget.value)}
          />
          <RichTextEditor
            label="Message"
            value={message}
            onChange={setMessage}
          />
          <Switch
            label={attachPdfLabel}
            checked={attachPdf}
            onChange={(event) => setAttachPdf(event.currentTarget.checked)}
          />
          <Group justify="flex-end">
            <SecondaryButton
              disabled={submitting}
              onClick={() => onSkip(buildRequest(true))}
            >
              Skip email
            </SecondaryButton>
            <BasicButton
              loading={submitting}
              disabled={to.length === 0}
              onClick={() => onSend(buildRequest(false))}
            >
              Send
            </BasicButton>
          </Group>
        </Stack>
      )}
    </BaseModal>
  );
};
